import React, { useState } from "react";
import VehicleTableData from "../../models/VehicleTableData";
import { ContainerForm } from "./styles";
import { Button, message, Modal, Table, Input } from "antd";
import api from "../../service/api";
import { useNavigate } from "react-router-dom";
import { environment } from "../../environments/environment";

const VehicleList: React.FC<{ vehicles?: VehicleTableData[] }> = ({ vehicles = [] }) => {
    const navigate = useNavigate();
    const ERROR_MESSAGES: Record<string, string> = {
        "Cannot cancel the request after the cancellation deadline.": "Não é possível cancelar a solicitação após o prazo de cancelamento."
    };
    const [isLoading, setIsLoading] = useState(false); // Estado de loading
    const [visible, setVisible] = useState(false); // Controle da visibilidade do modal
    const [vehicleToCancel, setVehicleToCancel] = useState<VehicleTableData | null>(null); // Armazena o veículo para cancelamento
    const [reason, setReason] = useState(""); // Estado para controlar o motivo do cancelamento

    const showCancelModal = (vehicle: VehicleTableData) => {
        setVisible(true); // Abre o modal
        setVehicleToCancel(vehicle); // Armazena o veículo selecionado
        setReason(""); // Limpa o campo de motivo ao abrir o modal
    };

    const handleOk = () => {
        // Verifica a condição e impede o fechamento caso o motivo não tenha 15 caracteres
        if (!reason.trim() || reason.trim().length < 15) {
            message.error("A descrição do cancelamento deve ter pelo menos 15 caracteres.");
            return; // Não fecha o modal
        }

        // Se o motivo for válido, continua com o cancelamento
        if (vehicleToCancel) {
            setIsLoading(true); // Ativa o indicador de loading
            handleCancel(vehicleToCancel, reason);
        }
    };

    const handleCancelModal = () => {
        console.log("Cancelamento abortado");
        setVisible(false); // Fecha o modal se o usuário cancelar
        setReason(""); // Limpa o campo de motivo ao fechar o modal
    };

    const handleCancel = (vehicle: VehicleTableData, reason: string) => {
        if (!vehicle) return;

        api.post(
            `scheduleExternal/cancelSchedule?fnRequisitionId=${vehicle.fnRequisitionId}&fcDetailDs=${encodeURIComponent(reason)}`,
            {},
            { baseURL: environment.baseURLScot20 }
        )
            .then(response => {
                if (response.data.success) {
                    message.success("Cancelamento realizado com sucesso.");
                    navigate("/"); // Redirecionando para a home
                } else {
                    // Obtém a mensagem da API e verifica se há uma tradução disponível
                    const apiMessage = response.data.message?.replace(/"/g, ""); // Remover aspas extras
                    const translatedMessage = Object.keys(ERROR_MESSAGES).find(key => apiMessage.includes(key))
                        ? ERROR_MESSAGES[apiMessage]
                        : apiMessage;

                    Modal.error({
                        title: "Erro ao cancelar",
                        content: translatedMessage,
                        okButtonProps: {
                            style: {
                                backgroundColor: "red",
                                borderColor: "darkred",
                            },
                        },
                    });
                }
            })
            .catch(error => {
                console.error("Erro na requisição:", error);
                console.error("Detalhes do erro:", error.response);
                message.error("Erro ao cancelar: " + (error.response?.data?.message || "Erro desconhecido."));
            })
            .finally(() => {
                setIsLoading(false); // Desativa o indicador de loading
                setVisible(false); // Fecha o modal após a operação
            });
    };

    const columns = [
        {
            title: "Placa",
            dataIndex: "fcVehicleLicPlateNu",
            key: "fcVehicleLicPlateNu",
        },
        {
            title: "Marca",
            dataIndex: "fcVehicleManufacturerNm",
            key: "fcVehicleManufacturerNm",
        },
        {
            title: "Modelo",
            dataIndex: "fcVehicleModelNm",
            key: "fcVehicleModelNm",
        },
        {
            title: "Cor",
            dataIndex: "fcVehicleColorNm",
            key: "fcVehicleColorNm",
        },
        {
            title: "Cancelar",
            key: "actions",
            render: (vehicle: VehicleTableData) =>
                !vehicle.denyCancel && (
                    <Button type="primary" danger onClick={() => showCancelModal(vehicle)}>
                        Cancelar
                    </Button>
                ),
        },
    ];

    return (
        <ContainerForm>
            <Table columns={columns} dataSource={vehicles} rowKey="fnRequisitionId" bordered />

            {/* Modal de Cancelamento */}
            <Modal
                title="Cancelar agendamento?"
                visible={visible} // Controlando a visibilidade
                width={600}
                onOk={handleOk} // Acionando a verificação e fechamento do modal
                onCancel={handleCancelModal}
                okText="Confirmar cancelamento"
                cancelText="Manter agendamento"
                closable={true}
                confirmLoading={isLoading} // Ativa o "loading" no botão OK
            >
                <p>Tem certeza de que deseja cancelar o agendamento? Por favor, informe o motivo do cancelamento abaixo.</p>
                <Input.TextArea
                    rows={3}
                    placeholder="Descreva aqui o motivo do cancelamento..."
                    value={reason} // Controlando o valor do campo com o estado
                    onChange={(e) => setReason(e.target.value)} // Atualiza o estado ao digitar
                />
            </Modal>
        </ContainerForm>
    );
};

export default VehicleList;
